import React from 'react';
import styled from 'styled-components';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import { Link } from 'react-router-dom';

import { AdminMenuCategory } from './AdminMenuCategory';
import { AdminPageLink } from './AdminPageLink';
import AnswersFillIcon from '@ad-scripts/presentation/components/common/icons/fill/answers-fill';
import { ChildrenContainer, LabelContainer } from '@ad-scripts/presentation/components/common/nav/menu_category';
import CommunicationsIcon from '@ad-scripts/presentation/components/common/icons/communications.jsx';
import ContentManagementFillIcon from '@ad-scripts/presentation/components/common/icons/fill/content-management-fill';
import CodeBranchFillIcon from '@ad-scripts/presentation/components/common/icons/fill/code_branch-fill';
import HeadsetIcon from '@ad-scripts/presentation/components/common/icons/headset.jsx';
import InsightsIcon from '@ad-scripts/presentation/components/common/icons/insights_icon';
import IntegrationsFillIcon from '@ad-scripts/presentation/components/common/icons/fill/integrations-fill';
import { NavMenu, NavMenuLabel } from '@ad-scripts/presentation/components/common/nav/nav_menu';
import PersonFillIcon from '@ad-scripts/presentation/components/common/icons/fill/person-fill';
import ProactiveIcon from '@ad-scripts/presentation/components/common/icons/stroke/proactive-stroke';
import { sortedNavItems } from './AdminSideNavHelpers';
import { StyledLinkOutStroke } from 'src/presentation/components/AgentMenu';
import SurveyFillIcon from '@ad-scripts/presentation/components/common/icons/fill/survey-fill';
import threadsResource from 'src/actions/threads/threadsResource';
import useCustomChannelsConfiguration from 'src/presentation/components/pages/shared/hooks/useCustomChannelsConfiguration';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import useResource from 'src/presentation/hooks/useResource';
import useSidekickV1Conf from 'src/presentation/components/pages/sidekick/threads/hooks/useSidekickConf';
import useThreadsConfiguration from 'src/presentation/components/pages/shared/hooks/useThreadsConfiguration';
import { CHAT_BRAND } from 'src/constants/branding';

export const AdminSideNav = (): JSX.Element => {
  const isFeatureEnabled = useIsFeatureEnabled();

  const { isFeatureEnabled: isThreadFeatureActivated } = useThreadsConfiguration();

  const { isFeatureEnabled: isCustomChannelsEnabled } = useCustomChannelsConfiguration();
  const { isSidekickThreadsEnabled } = useSidekickV1Conf();

  const orderedMenu = map(sortedNavItems, (item: any) => item.name);
  const mapNameToSortedChildren = reduce(
    sortedNavItems,
    (acc, item) => {
      acc[item.name] = item.children;
      return acc;
    },
    {}
  );

  function renderAnswerCategory() {
    return (
      <AdminMenuCategory label="Answers" icon={<AnswersFillIcon />} childrenOrder={mapNameToSortedChildren['Answers']}>
        <AdminPageLinkStyled label="Audiences" to="/audiences" roleFeature="answers:admin" />
        <AdminPageLinkStyled label="Help Center" to="/help-center" roleFeature="answers:admin" />
        {!isFeatureEnabled('disableKeywordGroup') && (
          <AdminPageLinkStyled label="Keyword Groups" to="/keyword-groups" roleFeature="answers:admin" />
        )}
        <AdminPageLinkStyled label="Languages" to="/answer-languages" roleFeature="answers:admin" />
      </AdminMenuCategory>
    );
  }

  function renderInsightsCategory() {
    return (
      <AdminMenuCategory label="Insights" icon={<InsightsIcon />} childrenOrder={mapNameToSortedChildren['Insights']}>
        <AdminPageLinkStyled
          label="Scheduled Reports"
          to="/scheduled-reports"
          roleFeature="scheduledReportsManagement"
        />
        <AdminPageLinkStyled label="SLA Business Hours" to="/sla-business-hours" roleFeature="rulesManagement" />
      </AdminMenuCategory>
    );
  }

  function renderRoutingCategory() {
    return (
      <AdminMenuCategory
        label="Orchestration"
        icon={<ContentManagementFillIcon />}
        childrenOrder={mapNameToSortedChildren['Orchestration']}
      >
        <AdminPageLinkStyled label="Business Hours" to="/business-hours" roleFeature="rulesManagement" />
        <AdminPageLinkStyled label="Entry Points" to="/entry-points" roleFeature="channelManagement" />
        <AdminPageLinkStyled
          label="Freeform Topics"
          to="/freeform-topics"
          roleFeature="customAttributeManagement"
          featureFlag="customAttributes"
        />
        <AdminPageLinkStyled label="Inboxes" to="/inboxes" roleFeature="inboxManagement" />
        <AdminPageLinkStyled
          label="Mobile Phone Number Identification"
          to="/mobile-number-identification"
          roleFeature="mobileNumberLookupManagement"
        />
        {isThreadFeatureActivated && (
          <NavThread label="Self-Service Threads" to="/threads" roleFeature="sidekickConfigurationManagement" />
        )}
        <AdminPageLinkStyled label="People Match" to="/people-match" roleFeature="rulesManagement" />
        <AdminPageLinkStyled label="Rules" to="/rules" roleFeature="rulesManagement" />
        <AdminPageLinkStyled label="Skills" to="/skills" roleFeature="skillManagement" />
        <AdminPageLinkStyled label="Topics" to="/topics" roleFeature="topics:admin" />
      </AdminMenuCategory>
    );
  }

  function renderSidekick() {
    if (!isSidekickThreadsEnabled) {
      return null;
    }

    return (
      <AdminMenuCategory
        label="Sidekick"
        icon={<CodeBranchFillIcon width="16" height="16" />}
        childrenOrder={mapNameToSortedChildren['Sidekick']}
      >
        <AdminPageLinkStyled label="Threads" to="/sidekick-threads" />
      </AdminMenuCategory>
    );
  }

  function renderAgentStatus() {
    return <AdminPageLinkStyled label="Agent Status" to="/agent-status" roleFeature="agentStatusManagement" />;
  }

  function renderCommunicationsCategory() {
    return (
      <AdminMenuCategory
        label="Channels"
        icon={<CommunicationsIcon />}
        childrenOrder={mapNameToSortedChildren['Channels']}
      >
        {isCustomChannelsEnabled && (
          <AdminPageLinkStyled label="Custom Channels" to="/custom-channels" roleFeature="channelManagement" />
        )}
        <AdminPageLinkStyled label="Email" to="/email" roleFeature="channelManagement" />
        <AdminPageLinkStyled label="Facebook Pages" to="/facebook" roleFeature="facebookAdminSettings" />
        <AdminPageLinkStyled label={'Instagram'} to="/instagram" roleFeature="facebookAdminSettings" />
        <AdminPageLinkStyled label="IVR" to="/ivrs" roleFeature="ivrConfigurationManagement" />
        <AdminPageLinkStyled label={CHAT_BRAND.name} to="/chat" roleFeature="sidekickConfigurationManagement" />
        <AdminPageLinkStyled label="SMS" to="/sms" roleFeature="channelManagement" />
        <AdminPageLinkStyled label="Voice" to="/voice" roleFeature="channelManagement" />
        <AdminPageLinkStyled label="WhatsApp" to="/whatsapp" roleFeature="channelManagement" />
      </AdminMenuCategory>
    );
  }

  const renderProactiveConversations = () => {
    return (
      <AdminMenuCategory
        label="Proactive Conversations"
        icon={<ProactiveIcon />}
        childrenOrder={mapNameToSortedChildren['Proactive Conversations']}
      >
        <AdminPageLinkStyled label="Proactive Chat" to="/proactive-chat" roleFeature="campaignsManagement" />
        <AdminPageLinkStyled label="Proactive Email" to="/proactive-email" roleFeature="campaignsManagement" />
        <AdminPageLinkStyled label="Proactive SMS" to="/proactive-sms" roleFeature="campaignsManagement" />
        <AdminPageLinkStyled label="Proactive Voice" to="/proactive-voice" roleFeature="campaignsManagement" />
      </AdminMenuCategory>
    );
  };

  const renderIntegrations = () => {
    return (
      <AdminMenuCategory
        label="App Developer Tools"
        icon={<IntegrationsFillIcon />}
        childrenOrder={mapNameToSortedChildren['App Developer Tools']}
      >
        <AdminPageLinkStyled
          label="App Actions"
          to="/app-actions"
          roleFeature="devManagement"
          featureFlag="showLasagnaPage"
        />
        <AdminPageLinkStyled label="API Tokens" to="/api-tokens" roleFeature="devManagement" />
        <AdminPageLinkStyled label="Apps" to="/apps" roleFeature="integrationsManagement" />
        <AdminPageLinkStyled label="Lookup Adapter Debugger" to="/lookupDebugger" roleFeature="debuggingView" />
        <AdminPageLinkStyled label="Webhooks" to="/webhooks" roleFeature="devManagement" />
      </AdminMenuCategory>
    );
  };

  const renderPeopleCategory = () => (
    <AdminMenuCategory label="People" icon={<PersonFillIcon />} childrenOrder={mapNameToSortedChildren['People']}>
      {(isFeatureEnabled('teamManagement') || isFeatureEnabled('userManagement')) && (
        <AdminPageLinkStyled label="Users" to="/users" />
      )}
      <AdminPageLinkStyled label="Teams" to="/teams" roleFeature="teamManagement" />
      <AdminPageLinkStyled label="Roles" to="/roles" roleFeature="roleManagement" />
    </AdminMenuCategory>
  );

  const renderCompliance = () => {
    return (
      <AdminMenuCategory
        label="Security and Compliance"
        icon={<SurveyFillIcon />}
        childrenOrder={mapNameToSortedChildren['Security and Compliance']}
      >
        <AdminPageLinkStyled label="Compliance Event Log" to="/compliance-events" roleFeature="complianceManagement" />
        <AdminPageLinkStyled label="Customer Data Retention" to="/data-retention" roleFeature="complianceManagement" />
        <AdminPageLinkStyled label="Single Sign-On" to="/sso" roleFeature="ssoConfigurationManagement" />
      </AdminMenuCategory>
    );
  };

  return (
    <NavMenuStyledContainer>
      <NavMenuStyled
        title="Settings"
        titleLink={<StyledLink to="/home">Settings</StyledLink>}
        childrenOrder={orderedMenu}
      >
        <AdminMenuCategory
          label="Agent Experience"
          icon={<HeadsetIcon />}
          childrenOrder={mapNameToSortedChildren['Agent Experience']}
        >
          {renderAgentStatus()}
          <AdminPageLinkStyled label="Conversation Workflow" to="/routing" roleFeature="channelManagement" />
          <AdminPageLinkStyled label="Idle Timing" to="/idle-timing" roleFeature="agentConfigurationManagement" />
          <AdminPageLinkStyled
            label="Relationships"
            to="/relationship-labels"
            roleFeature="relationshipLabelsManagement"
          />
        </AdminMenuCategory>
        {renderAnswerCategory()}
        {renderCommunicationsCategory()}
        {renderIntegrations()}
        {renderInsightsCategory()}
        {renderRoutingCategory()}
        {renderPeopleCategory()}
        {renderProactiveConversations()}
        {renderCompliance()}
        {renderSidekick()}
      </NavMenuStyled>
    </NavMenuStyledContainer>
  );
};

const NavThread = ({ label, to, roleFeature }): JSX.Element | null => {
  const { values: threads } = useResource(threadsResource);

  if (!threads?.length) {
    return null;
  }

  return <AdminPageLinkStyled label={label} to={to} roleFeature={roleFeature} />;
};

const NavMenuStyledContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  height: calc(100vh - 60px);
  margin: 0;
  overflow: auto;
  padding-top: 24px;
  padding-right: 24px;
  position: sticky;
  top: 60px;
  width: 344px;

  ${ChildrenContainer} {
    margin-left: 0;
    padding-bottom: 10px;
    width: 100%;
  }

  ${LabelContainer} {
    padding-left: 24px;
    text-transform: uppercase;

    a {
      color: ${p => p.theme.colors.gray800};
    }
  }

  ${NavMenuLabel} {
    padding-left: 24px;
  }
`;

const NavMenuStyled = styled(NavMenu)`
  min-width: 100%;
`;

const AdminPageLinkStyled = styled(AdminPageLink)`
  height: 32px;

  ${StyledLinkOutStroke} {
    position: relative;
    top: 2px;
  }
`;

const StyledLink = styled(Link)`
  color: ${p => p.theme.colors.black};
`;
